import {
  Box,
  CircularProgress,
  Chip,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import { useMemo } from "react";
import { OssStatusChip } from "../OssStatusChip";
import { riskLevel } from "../../constants/labels";
import { OssOrderMobileCard } from "./OssOrderMobileCard";
import { OssOrderToolbar } from "./OssOrderToolbar";
import { neutralChipSx, ossChipSx } from "../common/ossChipStyles";
import {
  buildOrderRowSummary,
  getOrderItemSummaryText,
  getOrderQuantitySummaryText,
} from "../../utils/orderStatusSummaryUtils";

const columns = [
  { label: "주문번호", sortBy: "orderNo" },
  { label: "품목 상태" },
  { label: "거래처", sortBy: "customerName" },
  { label: "품목", sortBy: "itemName" },
  { label: "착수 기준 여유일", sortBy: "marginDays" },
  { label: "요청 납기", sortBy: "requestedDueDate" },
  { label: "착수 필요일", sortBy: "requiredStartDate" },
  { label: "수량", sortBy: "quantity", align: "right" },
  { label: "일정 상태", sortBy: "scheduleStatus" },
  { label: "위험도", sortBy: "riskLevel" },
];

const OrderStatusSummary = ({ summary }) => {
  const entries = summary?.entries || [];
  return (
    <Stack direction="row" spacing={0.45} justifyContent="center" alignItems="center" flexWrap="wrap" useFlexGap>
      <Chip
        size="small"
        label={`품목 ${summary?.lineCount || 1}`}
        variant="outlined"
        sx={neutralChipSx}
      />
      {entries.length ? (
        entries.slice(0, 3).map((entry) => (
          <Chip
            key={entry.key}
            size="small"
            label={entry.label}
            variant="outlined"
            sx={ossChipSx({ tone: entry.tone, bg: entry.bg })}
          />
        ))
      ) : (
        <Chip
          size="small"
        label="모두 정상"
        variant="outlined"
        sx={ossChipSx({ tone: "#14b8a6", bg: "#ecfeff" })}
      />
      )}
    </Stack>
  );
};

const MarginCell = ({ value, meta }) => (
  <Stack spacing={0.25} alignItems="center">
    <Box
      component="span"
      sx={{
        minWidth: 62,
        px: 1.1,
        height: 26,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: `1px solid ${meta.tone}44`,
        borderRadius: "999px",
        bgcolor: meta.bg,
        color: meta.tone,
        fontSize: 12,
        fontWeight: "900 !important",
        lineHeight: 1,
        textAlign: "center",
      }}
    >
      {value ?? "-"}
      {value !== null && value !== undefined ? "일" : ""}
    </Box>
  </Stack>
);

export const OssOrderTable = ({ compact, filters, loading, viewData, selectedOrderId, onFilterChange, onOpen }) => {
  const rowSummaries = useMemo(
    () => new Map(viewData.items.map((row) => [String(row.order_id || row.order_no || row.order_line_id), buildOrderRowSummary(row)])),
    [viewData.items]
  );
  const getSummary = (row) => rowSummaries.get(String(row.order_id || row.order_no || row.order_line_id));
  const handleSort = (sortBy) => {
    if (!sortBy) return;
    const sameColumn = filters.sortBy === sortBy;
    onFilterChange({
      sortBy,
      sortDir: sameColumn && filters.sortDir === "ASC" ? "DESC" : "ASC",
    });
  };

  return (
    <Box
      sx={{
        overflow: "hidden",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        bgcolor: "#fff",
      }}
    >
      <OssOrderToolbar filters={filters} total={viewData.pagination.total} onFilterChange={onFilterChange} />

      {loading ? (
        <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}>
          <CircularProgress size={32} />
        </Box>
      ) : compact ? (
      <Stack spacing={1} sx={{ p: 1.5 }}>
        {viewData.items.length === 0 ? (
          <Box sx={{ minHeight: 280, display: "grid", placeItems: "center", color: "#6b7280" }}>
            조건에 맞는 주문이 없습니다.
          </Box>
        ) : (
          viewData.items.map((row) => (
            <OssOrderMobileCard
              key={row.order_line_id}
              row={row}
              summary={getSummary(row)}
              onOpen={() => onOpen(row.order_id || row.order_line_id)}
            />
          ))
        )}
      </Stack>
      ) : (
      <TableContainer
        sx={{
          minHeight: 520,
          maxHeight: "calc(100vh - 356px)",
          bgcolor: "#fff",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: 8, height: 8 },
          "&::-webkit-scrollbar-thumb": { borderRadius: 999, backgroundColor: "#cbd5e1" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#f8fafc" },
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: 1320 }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const active = filters.sortBy === column.sortBy;
                return (
                <TableCell
                  key={column.label}
                  align={column.align || "center"}
                  sx={{
                    height: 46,
                    px: 1.4,
                    fontSize: 12.5,
                    fontWeight: "900 !important",
                    color: "#374151",
                    bgcolor: "#f3f6fa",
                    borderColor: "#e1e6ef",
                  }}
                >
                  {column.sortBy ? (
                    <TableSortLabel
                      active={active}
                      direction={active && filters.sortDir === "DESC" ? "desc" : "asc"}
                      onClick={() => handleSort(column.sortBy)}
                      sx={{
                        "& .MuiTableSortLabel-icon": { fontSize: 17 },
                        "&.Mui-active": { color: "#4f46e5" },
                        "&.Mui-active .MuiTableSortLabel-icon": { color: "#4f46e5 !important" },
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {viewData.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ height: 320, color: "#6b7280" }}>
                  조건에 맞는 주문이 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              viewData.items.map((row) => {
                const riskMeta = riskLevel[row.risk_level] || riskLevel.NORMAL;
                const selected = String(row.order_id || row.order_line_id) === String(selectedOrderId);
                return (
                  <TableRow
                    key={row.order_line_id}
                    hover
                    onClick={() => onOpen(row.order_id || row.order_line_id)}
                    sx={{
                      "& td": {
                        height: 66,
                        px: 1.35,
                        borderColor: "#e8edf4",
                        fontSize: 13,
                        verticalAlign: "middle",
                      },
                      "&:nth-of-type(even)": { bgcolor: selected ? "#f5f3ff" : "#fbfcff" },
                      boxShadow: `inset 4px 0 0 ${riskMeta.tone}`,
                      bgcolor: selected ? "#f5f3ff" : "#fff",
                      cursor: "pointer",
                      "&:hover": { bgcolor: selected ? "#ede9fe" : "#fbfbfe" },
                    }}
                  >
                    <TableCell align="center">
                      <Typography sx={{ fontSize: 13.5, fontWeight: 800 }}>{row.order_no}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: "#6b7280" }}>
                        {row.customer_po_no || `품목 ${row.line_no}`}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: 180 }}>
                      <OrderStatusSummary summary={getSummary(row)} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography sx={{ fontSize: 13 }}>{row.customer_name}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: "#6b7280" }}>{row.site_name}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 310 }}>
                      <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 700 }}>
                        {getOrderItemSummaryText(row)}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: 11.5, color: "#6b7280" }}>
                        {row.item_code_snapshot} · {row.specification_snapshot || "규격 미등록"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center"><MarginCell value={row.available_margin_days} meta={riskMeta} /></TableCell>
                    <TableCell align="center">{row.requested_due_date}</TableCell>
                    <TableCell align="center">{row.required_start_date || "-"}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontSize: 13.5, fontWeight: "800 !important", color: "#111827" }}>
                        {getOrderQuantitySummaryText(row)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center"><OssStatusChip value={row.schedule_status} /></TableCell>
                    <TableCell align="center"><OssStatusChip type="risk" value={row.risk_level} /></TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <Box
      sx={{
        py: 1.3,
        px: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderTop: "1px solid #e6eaf0",
        bgcolor: "#fbfbfe",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "#6b7280" }}>
        <ViewWeekOutlinedIcon fontSize="small" />
        <Typography sx={{ fontSize: 12.5 }}>주문번호 단위 표시</Typography>
      </Box>
      <Pagination
        page={viewData.pagination.page || 1}
        count={viewData.pagination.totalPages || 1}
        onChange={(_, page) => onFilterChange("page", page)}
        color="primary"
        size="small"
      />
    </Box>
    </Box>
  );
};
